import { bridge } from './bridge';
import { dismissNotices } from './dialogs';
import { readRows } from './rows';
import { onScreen, onScreenAll } from './visible';

/**
 * **미결함에서 결재한다** — 승인·반려·전결.
 *
 * 미결함(`/w_tb_e080_list`)은 목록(`incompleteBoxList`)에서 문서를 **더블클릭**해야 결재 창이 열린다.
 * 창은 클릭한 행이 아니라 **선택된 행**(`focusedIncompleteBox`)을 연다 — 그래서 먼저 고르고(`focusRow`)
 * 그 행에 dblclick 만 보낸다(click 을 같이 보내면 300ms 막이가 선택을 다른 행으로 옮길 수 있다).
 * 창의 「승인/반려/전결」은 묻지 않고 바로 서버로 간다(앱 `IncompleteBox.runApproval`). 성공·실패는
 * 서버 문구 알림으로 오고, 성공하면 창이 닫히고 목록을 다시 조회한다 — 그 문서가 목록에서 빠진다.
 *
 * 사람은 15일에 1,298번(56명·27테넌트) 승인했다. 대개 줄줄이 묶어서(904건). 사용자 결정(2026-10-13)으로
 * 연다. 무엇을 결재할지는 런타임이 사람에게 한 번 확인받은 뒤 여기로 넘긴다.
 */
export type ApprovalAction = '승인' | '반려' | '전결';

export type ApprovalResult = { appnum: string; done: boolean; notice: string };

const LIST = 'incompleteBoxList';
const MODAL_MS = 15000;
const POLL_MS = 150;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/** 결재 창 — 제목이 「결재승인[…]」인 보이는 창. */
function approvalModal(): HTMLElement | undefined {
  return onScreenAll('[class*=ModalView]')
    .find((one) => /결재승인/.test(one.textContent ?? '') && Array.from(one.querySelectorAll('button'))
      .some((button) => (button.textContent ?? '').trim() === '승인'));
}

async function until<T>(read: () => T | undefined | false, ms: number): Promise<T | undefined> {
  const deadline = Date.now() + ms;
  for (;;) {
    const value = read();
    if (value) return value;
    if (Date.now() >= deadline) return undefined;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
}

/** 여러 줄 입력칸에 값을 넣는다 — React 가 알아듣게 원래 setter 로. */
function typeInto(box: HTMLTextAreaElement | HTMLInputElement, value: string): void {
  const proto = box instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(box, value);
  box.dispatchEvent(new Event('input', { bubbles: true }));
  box.dispatchEvent(new Event('change', { bubbles: true }));
}

/** 한 문서를 결재한다. 목록이 이미 그 화면에 떠 있어야 한다. */
export async function approveOne(appnum: string, action: ApprovalAction, remark = ''): Promise<ApprovalResult> {
  const focus = bridge().focusRow;
  if (!focus) throw new Error('이 앱은 목록의 행을 고르는 조작을 지원하지 않는다');
  if (!readRows(LIST).some((row) => String(row.appnum ?? '') === appnum)) {
    return { appnum, done: false, notice: '미결함 목록에 그 문서가 없다' };
  }
  if (!await focus(LIST, { appnum })) return { appnum, done: false, notice: '그 문서를 고르지 못했다' };
  await sleep(300);
  const index = readRows(LIST).findIndex((row) => String(row.appnum ?? '') === appnum);
  const row = onScreenAll(`[data-el-grid="${LIST}"] [data-el-row="${index}"]`)[0]
    ?? onScreenAll(`[data-el-grid="${LIST}"] [data-el-row]`)[0];
  if (!row) return { appnum, done: false, notice: '목록의 행을 찾지 못했다' };
  row.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, cancelable: true, detail: 2 }));
  const modal = await until(approvalModal, MODAL_MS);
  if (!modal) return { appnum, done: false, notice: '결재 창이 열리지 않았다' };
  if (remark) {
    const box = modal.querySelector<HTMLTextAreaElement | HTMLInputElement>('textarea, input[type="text"]');
    if (!box) return { appnum, done: false, notice: '지시사항 칸을 찾지 못했다' };
    typeInto(box, remark);
    await sleep(200);
  }
  // 창은 문서를 불러오는 동안 「문서 정보를 불러오는 중입니다」로 막는다 — 단추가 풀릴 때까지 본다.
  const button = await until(() => Array.from(modal.querySelectorAll<HTMLButtonElement>('button'))
    .find((one) => (one.textContent ?? '').trim() === action && !one.disabled), MODAL_MS);
  if (!button) return { appnum, done: false, notice: `${action} 단추를 누를 수 없었다` };
  await sleep(500);
  button.click();
  // 서버 문구 알림을 받는다. 성공이면 창이 닫히고 목록이 다시 온다.
  const notices: string[] = [];
  const closed = await until(() => {
    return !onScreen(modal) || !approvalModal();
  }, MODAL_MS);
  notices.push(...await dismissNotices(3, 2000));
  await sleep(800);
  const gone = !readRows(LIST).some((row) => String(row.appnum ?? '') === appnum);
  const said = notices.join(' / ');
  // 창이 닫히고 목록에서 빠졌어야 결재된 것이다. 「오류」·「실패」 알림이면 아니다.
  const done = Boolean(closed) && gone && !/오류|실패/.test(said);
  return { appnum, done, notice: said || (done ? `${action}했다` : '창이 그대로다 — 결재되었는지 확인하지 못했다') };
}
